export const About = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-6 animate-fade-in">
              Our Story
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.9] mb-8 animate-fade-in-up">
              <span className="gold-gradient">About</span>
              <br />
              <span className="text-white/90">Goldnote Corporation</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 font-light leading-relaxed max-w-2xl animate-fade-in-up-delay-1">
              We are pioneering a new standard in precious metals, combining traditional 
              craftsmanship with modern innovation to create gold notes that are both 
              beautiful and valuable.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="mission-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
                Our Mission
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white/90 mb-6">
                Democratizing Gold Ownership
              </h2>
              <p className="text-white/60 font-light leading-relaxed mb-6">
                Goldnote Corporation was founded with a clear vision: to make gold ownership 
                accessible to everyone. Our innovative gold note format allows investors and 
                collectors to own fractional amounts of pure gold in a convenient, portable format.
              </p>
              <p className="text-white/60 font-light leading-relaxed">
                Each gold note represents a specific weight of 24-karat gold, authenticated 
                and guaranteed for purity. Whether you're a seasoned investor or just starting 
                your precious metals journey, Goldnote offers a unique way to diversify your portfolio.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-[#121212] border border-white/5 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_goldnote-portal/artifacts/mmafn7da_1%20Gram%20100%20pc%20Laser%20Tray%20with%20Fiches.jpeg"
                  alt="Goldnote precision manufacturing"
                  className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-[#D4AF37]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20" data-testid="values-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              What We Stand For
            </p>
            <h2 className="font-serif text-3xl md:text-4xl gold-gradient">Our Core Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <div className="w-12 h-12 mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif text-xl">I</span>
              </div>
              <h3 className="font-serif text-xl text-white/90 mb-3">Integrity</h3>
              <p className="text-sm text-white/60 font-light">
                Every product we create meets the highest standards of authenticity and quality.
              </p>
            </div>

            <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <div className="w-12 h-12 mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif text-xl">II</span>
              </div>
              <h3 className="font-serif text-xl text-white/90 mb-3">Innovation</h3>
              <p className="text-sm text-white/60 font-light">
                We continuously push the boundaries of what's possible in precious metals.
              </p>
            </div>

            <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <div className="w-12 h-12 mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif text-xl">III</span>
              </div>
              <h3 className="font-serif text-xl text-white/90 mb-3">Excellence</h3>
              <p className="text-sm text-white/60 font-light">
                Craftsmanship and attention to detail define everything we do.
              </p>
            </div>

            <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <div className="w-12 h-12 mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif text-xl">IV</span>
              </div>
              <h3 className="font-serif text-xl text-white/90 mb-3">Trust</h3>
              <p className="text-sm text-white/60 font-light">
                Building lasting relationships through transparency and reliability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="quality-section">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              Quality Assurance
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white/90 mb-8">
              Certified & Authenticated
            </h2>
            <p className="text-white/60 font-light leading-relaxed mb-8">
              Every Goldnote product undergoes rigorous testing and verification. Our gold notes 
              are crafted from 24-karat gold and come with full documentation of authenticity. 
              We partner with industry-leading assayers and mints to ensure that what you receive 
              meets the highest standards in the precious metals industry.
            </p>
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="text-center">
                <p className="font-serif text-4xl gold-gradient">99.99%</p>
                <p className="text-xs text-white/50 tracking-wider uppercase mt-2">Pure Gold</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-4xl gold-gradient">100%</p>
                <p className="text-xs text-white/50 tracking-wider uppercase mt-2">Authenticated</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-4xl gold-gradient">4+</p>
                <p className="text-xs text-white/50 tracking-wider uppercase mt-2">Product Lines</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
